<template>
    <ValidationProvider 
        :name="field"
        :rules="rules"
        v-slot="{ errors }"
        slim
    >
        <v-text-field
            ref="addressInput"
            v-model="innerValue"
            :error-messages="errors"
            :label="label"
            :disabled="disabled"
            :clearable="clearable"
            depressed 
            :class="withMargin ? 'ml-4 mr-4' : ''" 
            @click:clear="resetVal()"
            @input="onInput"
            @focus="onFocus"
            @blur="onBlur"
            @keydown.esc="closeMenu"
            :alvqa="alvqa"
            :loading="isLoading"
            autocomplete="chrome-off"
            :name="'address-' + Math.random().toString(36).substr(2, 9)"
        />
        <slot name="append-icon"></slot>

        <!-- Autocomplete Dropdown -->
        <v-list 
            v-if="showMenu && predictions.length > 0" 
            v-click-outside="closeMenu"
            dense 
            class="google-places-dropdown"
        >
            <v-list-item
                v-for="(prediction, index) in predictions"
                :key="prediction.placeId"
                @click="selectPrediction(prediction)"
                :alvqa="alvqa + '-prediction-' + index"
            >
                <v-list-item-icon class="mr-2">
                    <v-icon small>place</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title>{{ prediction.mainText }}</v-list-item-title>
                    <v-list-item-subtitle>{{ prediction.secondaryText }}</v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>
        </v-list>
    </ValidationProvider>
</template>

<script>
import VTextField from 'vuetify/es5/components/VTextField'
import api from '@/api_alv.js'

export default {
    name: "BaseEditPageGoogleAddressAutocomplete",
    extends: VTextField,
    props: {
        context: String,
        field: String,
        required: Boolean,
        clearable: {
            default: true
        },
        disabled: {
            default: false
        },
        rules: {
            type: [Object, String],
            default: ""
        },
        withMargin: {
            type: Boolean,
            default: true
        },
        alvqa: {
            type: String,
            default: ''
        },
    },
    data() {
        return {
            predictions: [],
            showMenu: false,
            isLoading: false,
            sessionToken: null,
            debouncedSearch: null,
            isFocused: false,
            lastDetectedLanguage: null
        }
    },
    computed: {
        innerValue: {
            get() { 
                return this.$store.getters['get' + this.context + 'Details' + this.field]
            },
            set(value) { 
                this.$store.commit('set'+ this.context + 'Details' + this.field, value) 
            }
        }
    },
    methods: {
        generateSessionToken() {
            // Generate a random session token for cost optimization
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = Math.random() * 16 | 0
                const v = c === 'x' ? r : (r & 0x3 | 0x8)
                return v.toString(16)
            })
        },

        onFocus() {
            this.isFocused = true
            if (!this.sessionToken) {
                this.sessionToken = this.generateSessionToken()
            }
        },

        onBlur() {
            setTimeout(() => {
                this.isFocused = false
                this.closeMenu()
            }, 200)
        },

        closeMenu() {
            this.showMenu = false
            this.predictions = []
        },

        onInput(value) {
            if (this.debouncedSearch) {
                this.debouncedSearch()
            }
        },

        async searchAutocomplete() {
            const input = this.innerValue

            if (!input || input.length < 3) {
                this.predictions = []
                this.showMenu = false
                return
            }

            this.isLoading = true

            try {
                const detectedLanguage = this.detectInputLanguage(input)
                const response = await api.triggerAction(
                    'google/places',
                    { input: input, sessionToken: this.sessionToken, language: detectedLanguage },
                    { action: 'autocomplete' }
                )

                if (response && response.error === -1 && response.response && response.response.predictions) {
                    this.predictions = response.response.predictions
                    this.showMenu = this.predictions.length > 0 && this.isFocused
                } else {
                    this.predictions = []
                    this.showMenu = false
                }
            } catch (error) {
                console.error('Google Places Autocomplete error:', error)
                this.predictions = []
                this.showMenu = false
            } finally {
                this.isLoading = false
            }
        },

        async selectPrediction(prediction) {
            this.showMenu = false
            this.isLoading = true

            try {
                const response = await api.triggerAction(
                    'google/places',
                    { placeId: prediction.placeId, sessionToken: this.sessionToken, language: this.lastDetectedLanguage },
                    { action: 'details' }
                )

                if (response && response.error === -1 && response.response && response.response.address) {
                    this.setAddressFields(response.response.address)
                }

                // Generate new session token for next search
                this.sessionToken = this.generateSessionToken()

            } catch (error) {
                console.error('Google Places Details error:', error)
            } finally {
                this.isLoading = false
                this.predictions = []
            }
        },

        setAddressFields(addressData) {
            // Clear all address fields first, then set new values
            // This ensures old values don't persist when new address has empty fields
            this.$store.commit(`set${this.context}DetailsAddressRowOne`, addressData.addressRowOne || null)
            this.$store.commit(`set${this.context}DetailsAddressRowTwo`, null)
            this.$store.commit(`set${this.context}DetailsZip`, addressData.zip || null)
            this.$store.commit(`set${this.context}DetailsCity`, addressData.city || null)
            this.$store.commit(`set${this.context}DetailsState`, addressData.state || null)
            this.$store.commit(`set${this.context}DetailsCountry`, addressData.country || null)
        },

        async resetVal() {
            await this.$nextTick()
            this.innerValue = null
            this.predictions = []
            this.showMenu = false
        },

        /**
         * Detect language based on input characters
         * Returns 'ja' for Japanese, 'ko' for Korean, 'en' for Latin characters
         */
        detectInputLanguage(text) {
            if (!text) return 'en'

            // Japanese: Hiragana, Katakana, or Kanji
            const hasHiragana = /[\u3040-\u309F]/.test(text)
            const hasKatakana = /[\u30A0-\u30FF]/.test(text)
            const hasKanji = /[\u4E00-\u9FAF]/.test(text)

            // Korean: Hangul
            const hasHangul = /[\uAC00-\uD7AF\u1100-\u11FF]/.test(text)

            if (hasHiragana || hasKatakana || hasKanji) {
                this.lastDetectedLanguage = 'ja'
                return 'ja'
            }

            if (hasHangul) {
                this.lastDetectedLanguage = 'ko'
                return 'ko'
            }

            // Latin/Western characters: always use English for consistent romanized results
            this.lastDetectedLanguage = 'en'
            return 'en'
        }
    },
    created() {
        // Debounce search to avoid too many API calls
        this.debouncedSearch = _.debounce(this.searchAutocomplete, 400)
    },
    beforeDestroy() {
        if (this.debouncedSearch) {
            this.debouncedSearch.cancel()
        }
    }
}
</script>

<style scoped>
.google-places-dropdown {
    position: absolute;
    z-index: 999;
    background: white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    max-height: 300px;
    overflow-y: auto;
    width: 100%;
    margin-top: -20px;
}
</style>
